import { clsx, type ClassValue } from "clsx";

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/* ---------------------------------- types --------------------------------- */

export type Status = "scheduled" | "completed" | "cancelled";

export const STATUS_ORDER: Status[] = ["scheduled", "completed", "cancelled"];

export const STATUS_META: Record<Status, { label: string; color: string; description: string }> = {
  scheduled: { label: "Scheduled", color: "#5B6B8C", description: "On the calendar — awaiting" },
  completed: { label: "Completed", color: "#1F8A70", description: "Wrapped up" },
  cancelled: { label: "Cancelled", color: "#B4443C", description: "Called off" },
};

export const EMPLOYEE_COLORS = [
  "#E4572E",
  "#1F8A70",
  "#4C5FD5",
  "#D69E2E",
  "#A13D63",
  "#5B8C3E",
  "#4E6E8E",
  "#8C5A3C",
];

export const TEAM_COLORS = [
  "#4C5FD5",
  "#E4572E",
  "#1F8A70",
  "#A13D63",
  "#D69E2E",
  "#5B8C3E",
  "#4E6E8E",
  "#7A4DA8",
];

export type Team = {
  id: number;
  name: string;
  color: string;
};

export type Employee = {
  id: number;
  name: string;
  role: string;
  color: string;
  teamId: number | null;
  boundAccountId: string | null;
  boundAccountName: string | null;
  boundAccountEmail: string | null;
};

export type CurrentUser = {
  id: string;
  email: string;
  full_name?: string;
  role?: "admin" | "employee" | "super_admin" | string;
  is_super_admin?: boolean;
};

export type Itinerary = {
  id: number;
  employeeId: number;
  title: string;
  notes: string;
  date: string; // yyyy-MM-dd
  appointmentTime: string; // "HH:mm" or ""
  seq: number;
  locationName: string;
  lat: number | null;
  lng: number | null;
  status: Status;
  employeeName: string | null;
  employeeColor: string | null;
  teamId: number | null;
};

export type PickedLocation = { name: string; lat: number; lng: number };

/* ---------------------------------- scope --------------------------------- */

/** Combined team + employee filter used by every view. */
export type Scope = { team: number | "all"; employee: number | "all" };

export const ALL_SCOPE: Scope = { team: "all", employee: "all" };

/** Employees visible under the current team selection. */
export function scopedEmployees(employees: Employee[], scope: Scope) {
  return scope.team === "all" ? employees : employees.filter((e) => e.teamId === scope.team);
}

/** Employees actually selected (respects the employee drill-down). */
export function targetEmployees(employees: Employee[], scope: Scope) {
  const pool = scopedEmployees(employees, scope);
  return scope.employee === "all" ? pool : pool.filter((e) => e.id === scope.employee);
}

/** Does this itinerary belong to the current team + employee selection? */
export function inScope(it: Itinerary, scope: Scope, teamOf: Map<number, number | null>) {
  if (scope.employee !== "all") return it.employeeId === scope.employee;
  if (scope.team === "all") return true;
  return (teamOf.get(it.employeeId) ?? null) === scope.team;
}

/** employeeId → teamId lookup, resilient to optimistic team reassignments. */
export function teamIndex(employees: Employee[]) {
  return new Map<number, number | null>(employees.map((e) => [e.id, e.teamId]));
}

/** Human label for the active scope, e.g. "Logistics · Jonas". */
export function scopeLabel(scope: Scope, teams: Team[], employees: Employee[]) {
  const team = teams.find((t) => t.id === scope.team);
  const emp = employees.find((e) => e.id === scope.employee);
  if (emp && team) return `${team.name} · ${emp.name.split(" ")[0]}`;
  if (emp) return emp.name;
  if (team) return team.name;
  return "All teams";
}

/* --------------------------------- helpers -------------------------------- */

export function ordinal(n: number) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/** "14:30" → "2:30 PM" */
export function formatTime(value: string) {
  if (!/^\d{2}:\d{2}$/.test(value)) return "";
  const [h, m] = value.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${suffix}`;
}

export function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

/** Append an alpha byte to a #rrggbb hex color. */
export function withAlpha(hex: string, alpha: number) {
  const h = hex.replace("#", "");
  const a = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
    .toString(16)
    .padStart(2, "0");
  return `#${h}${a}`;
}

/** Local-timezone safe yyyy-MM-dd (unlike Date#toISOString). */
export function iso(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse a yyyy-MM-dd string as a local date. */
export function parseIso(s: string) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/** Sort helper: timed stops first (by clock), then untimed, then by sequence. */
export function bySchedule(a: Itinerary, b: Itinerary) {
  if (a.appointmentTime && b.appointmentTime && a.appointmentTime !== b.appointmentTime) {
    return a.appointmentTime < b.appointmentTime ? -1 : 1;
  }
  return a.seq - b.seq || a.id - b.id;
}
